import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { colorForAgent } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { AgentDot } from '../../../AgentDot';
import {
  AGENT_NODE_SIZE,
  AGENT_RING_R,
  GENOME_SIZE,
  RING_RADII,
  agentNodeAngles,
  polarToOffset,
  radialFlightOffset,
  shortSkillLabel,
  skillLabelPlacement,
  skillSlots,
  slotHalf,
  slotSide,
} from '../../../../lib/genome-layout';
import {
  AGENT_NODE_LAND_MS,
  AGENT_STAGGER_MS,
  LABEL_FADE_MS,
  LABEL_LAND_SCALE,
  LABEL_STAGGER_MS,
  MAESTRO_LAND_MS,
  REEL_START_MS,
  RING_SWEEP_STAGGER_MS,
  SKILL_DOT_FLIGHT_MS,
  SKILL_DOT_STAGGER_MS,
  genomeTimeline,
} from '../../../../lib/genome-timing';
import { MaestroMark } from '../../MaestroMark';
import { agentControlProps } from '../../../../lib/agent-deselect';
import styles from './Genome.module.css';

// The genome — the Your Agents hero, ported from the native `AgentConstellation`.
//
// Three dashed rings sweep in, the team's skills fly out from the centre as dots
// onto the two outer rings, the agents land one by one on the inner ring, and the
// Maestro pops in at the middle. Then the figure simply settles at rest — there is
// no final revolution. Geometry lives in `lib/genome-layout`, the beats in
// `lib/genome-timing`; both are pure and unit-tested.
//
// The reel is CSS: every element carries its own delay off the shared timeline, so
// there is no per-frame React work and nothing re-renders mid-animation. It plays
// once per page load — a route change back to this screen finds the figure at rest,
// matching native's session latch — and is skipped under `prefers-reduced-motion`.
//
// On desktop, hovering a skill dot names the skill: the web design's replacement for
// native's label beat (Figma `10261:86167` draws the dots bare). On mobile (`nameReel`)
// the reel plays native's names beat instead — every name flashes in and out during
// arrival — and hover still names a dot once the figure has settled. Clicking an agent
// selects it, which dims the rest of the figure and brings its card to the front of the pile.

export interface GenomeAgent {
  id: string;
  name: string;
}

interface Props {
  agents: GenomeAgent[];
  skillNames: string[];
  maestroName: string;
  /** The Maestro's pile id — the figure and the card pile share one selection. */
  maestroId: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Play native's skill-name beat during arrival (mobile). Desktop leaves names to hover. */
  nameReel?: boolean;
}

/** The names beat's phase, while the mobile reel plays: labels fade in one by one (`in`),
 *  then fade out together (`out`). `idle` is before they appear and after the figure settles. */
type NamesPhase = 'idle' | 'in' | 'out';

// Once per page load, like native's `store.agentsHeroPlayed`: the reel is an arrival
// moment, not something to replay every time the screen is visited.
//
// Claimed when the reel FINISHES, never on mount — a figure torn down mid-arrival never
// really played, so it must not spend the one reel this page load gets. That is load-bearing
// for Your Agents: `useNarrow` reads false until its effect runs, so on a narrow client the
// WIDE screen mounts a Genome for one frame before the narrow one replaces it. Claiming on
// mount left the mobile figure permanently static, because React computes the incoming
// component's state during render — before the outgoing one's cleanup could release a latch.
let reelPlayed = false;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function Genome({ agents, skillNames, maestroName, maestroId, selectedId, onSelect, nameReel = false }: Props) {
  const maskId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  // The whole team rides the ring — a roster past ten spreads tighter around it rather than
  // being cut off (mirrors the card pile, which shows every agent). Native still caps at ten.
  const nodes = useMemo(() => agents, [agents]);
  const slots = useMemo(() => skillSlots(skillNames), [skillNames]);
  const angles = useMemo(() => agentNodeAngles(nodes.length), [nodes.length]);
  const timeline = useMemo(
    () => genomeTimeline(nodes.length, { nameReel, skillCount: slots.length }),
    [nodes.length, nameReel, slots.length],
  );

  // Decided once, on mount: replaying the reel whenever the roster refetches would
  // make the figure twitch every time a task lands.
  const [animate] = useState(() => !reelPlayed && !prefersReducedMotion());
  const [settled, setSettled] = useState(!animate);
  const [namesPhase, setNamesPhase] = useState<NamesPhase>('idle');
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!animate) return;
    settleTimer.current = setTimeout(() => {
      reelPlayed = true;
      setSettled(true);
    }, timeline.settledAtMs);
    return () => clearTimeout(settleTimer.current);
  }, [animate, timeline.settledAtMs]);

  // The mobile names beat: flash the skill names in one by one, then fade them out together —
  // two scheduled transitions, exactly as native drives its phases. Desktop never enters it.
  useEffect(() => {
    if (!animate || !nameReel) return;
    const inAt = setTimeout(() => setNamesPhase('in'), timeline.namesAtMs);
    const outAt = setTimeout(() => setNamesPhase('out'), timeline.namesHideAtMs);
    return () => {
      clearTimeout(inAt);
      clearTimeout(outAt);
    };
  }, [animate, nameReel, timeline.namesAtMs, timeline.namesHideAtMs]);

  // The beat only drives the labels while the reel plays; once settled, hover takes over — so a
  // held `out` fill can't out-specificity the hover rule and pin the names invisible.
  const namesBeat = animate && nameReel && !settled && namesPhase !== 'idle' ? namesPhase : undefined;

  const selecting = selectedId !== null;
  const maestroSelected = selectedId === maestroId;

  return (
    <div className={styles.stageBox}>
      <div
        className={animate ? `${styles.stage} ${styles.reel}` : styles.stage}
        style={
          {
            '--genome-agents-at': `${timeline.agentsAtMs}ms`,
            '--genome-maestro-at': `${timeline.maestroAtMs}ms`,
          } as CSSProperties
        }
      >
        <svg
          className={styles.rings}
          viewBox={`0 0 ${GENOME_SIZE} ${GENOME_SIZE}`}
          data-dimmed={selecting || undefined}
          aria-hidden
          focusable="false"
        >
          <defs>
            {RING_RADII.map((r, i) => (
              <mask
                key={r}
                id={`${maskId}-ring-${i}`}
                maskUnits="userSpaceOnUse"
                maskContentUnits="userSpaceOnUse"
                x="0"
                y="0"
                width={GENOME_SIZE}
                height={GENOME_SIZE}
              >
                {/* The sweep: one mask dash grows from nothing to the full
                    circumference — SwiftUI's `trim(to:)` expressed in SVG. */}
                <circle
                  className={styles.ringMask}
                  cx={GENOME_SIZE / 2}
                  cy={GENOME_SIZE / 2}
                  r={r}
                  pathLength={1}
                  style={{ animationDelay: `${REEL_START_MS + i * RING_SWEEP_STAGGER_MS}ms` }}
                />
              </mask>
            ))}
          </defs>
          {RING_RADII.map((r, i) => (
            <circle
              key={r}
              className={styles.ring}
              cx={GENOME_SIZE / 2}
              cy={GENOME_SIZE / 2}
              r={r}
              mask={`url(#${maskId}-ring-${i})`}
            />
          ))}
        </svg>

        <div className={styles.orbitLayer}>
          {slots.map((slot) => (
            <SkillDot key={slot.index} slot={slot} dimmed={selecting} labelsEnabled={settled} />
          ))}
        </div>

        {/* The mobile names beat draws its labels in their OWN radial layer — native's
            `skillLabel` placement (anchored just outside each dot, side-aligned, clamped to the
            figure) — rather than on the dots. Desktop never renders it; hover names stay on the dot. */}
        {nameReel && (
          <div
            className={styles.namesLayer}
            data-names={namesBeat}
            style={{ '--label-fade': `${LABEL_FADE_MS}ms`, '--label-land-scale': LABEL_LAND_SCALE } as CSSProperties}
            aria-hidden
          >
            {slots.map((slot) => {
              const at = skillLabelPlacement(slot.r, slot.angleDeg);
              return (
                <span
                  key={slot.index}
                  className={styles.nameLabel}
                  style={
                    {
                      left: `${at.x}px`,
                      top: `${at.y}px`,
                      width: `${at.width}px`,
                      textAlign: at.align,
                      '--label-in-delay': `${slot.index * LABEL_STAGGER_MS}ms`,
                    } as CSSProperties
                  }
                >
                  {shortSkillLabel(slot.name)}
                </span>
              );
            })}
          </div>
        )}

        <div className={styles.orbitLayer}>
          {nodes.map((agent, i) => {
            const selected = selectedId === agent.id;
            const key = agent.id || agent.name;
            return (
              <button
                key={key}
                type="button"
                className={styles.node}
                {...agentControlProps}
                aria-pressed={selected}
                data-selected={selected || undefined}
                data-dimmed={(selecting && !selected) || undefined}
                onClick={() => onSelect(agent.id)}
                style={
                  {
                    ...polarToOffset(AGENT_RING_R, angles[i]),
                    '--node-accent': colorForAgent(key),
                    animationDelay: `calc(var(--genome-agents-at) + ${i * AGENT_STAGGER_MS}ms)`,
                    animationDuration: `${AGENT_NODE_LAND_MS}ms`,
                  } as CSSProperties
                }
              >
                <span className={styles.nodeGlow} aria-hidden />
                <AgentDot id={key} size={AGENT_NODE_SIZE} />
                <span className={styles.label}>{agent.name}</span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className={styles.maestro}
          {...agentControlProps}
          aria-pressed={maestroSelected}
          data-dimmed={(selecting && !maestroSelected) || undefined}
          onClick={() => onSelect(maestroId)}
          style={{
            animationDelay: 'var(--genome-maestro-at)',
            animationDuration: `${MAESTRO_LAND_MS}ms`,
          }}
        >
          <MaestroMark className={styles.maestroMark} />
          <span className={styles.label}>{maestroName}</span>
        </button>
      </div>
    </div>
  );
}

// A skill's dot: a 10px mark inside a 40px hit target, matching native's oversized
// tap area for an 8pt dot. The name rides in a label that flips to whichever side
// of the figure keeps it clear of the graphic — shown on hover/focus once settled,
// and flashed in-and-out during the mobile arrival reel (`namesBeat`).
function SkillDot({
  slot,
  dimmed,
  labelsEnabled,
}: {
  slot: { index: number; r: number; angleDeg: number; name: string };
  dimmed: boolean;
  labelsEnabled: boolean;
}) {
  const { dx, dy } = radialFlightOffset(slot.r, slot.angleDeg);
  return (
    <button
      type="button"
      className={styles.skillDot}
      data-side={slotSide(slot.angleDeg)}
      data-half={slotHalf(slot.angleDeg)}
      data-dimmed={dimmed || undefined}
      data-labels={labelsEnabled || undefined}
      style={
        {
          ...polarToOffset(slot.r, slot.angleDeg),
          '--fly-dx': `${dx}px`,
          '--fly-dy': `${dy}px`,
          animationDelay: `${REEL_START_MS + slot.index * SKILL_DOT_STAGGER_MS}ms`,
          animationDuration: `${SKILL_DOT_FLIGHT_MS}ms`,
        } as CSSProperties
      }
    >
      <span className={styles.skillMark} aria-hidden />
      <span className={styles.skillLabel}>{shortSkillLabel(slot.name)}</span>
    </button>
  );
}
